import { Building2, Plus, Search, UserRound, UsersRound, X } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, rows } from "../../lib/api";
import type { ApiList } from "../../lib/types";
import type { Tiers, TiersRoleCode, TiersType } from "./types";
import { tiersRoleLabels, tiersRoles } from "./types";

const emptyForm = {
  type_tiers: "ORGANIZATION" as TiersType,
  nom: "", prenom: "", raison_sociale: "", email: "", telephone: "", whatsapp: "",
  identifiant_fiscal: "", registre_commerce: "", role_codes: ["CLIENT"] as TiersRoleCode[],
};

export function TiersPage() {
  const navigate = useNavigate();
  const [items,setItems]=useState<Tiers[]>([]);
  const [search,setSearch]=useState("");
  const [role,setRole]=useState("");
  const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(false);
  const [form,setForm]=useState(emptyForm);
  const [error,setError]=useState("");

  async function load(){
    setLoading(true);
    try{
      const q=new URLSearchParams();
      if(search.trim())q.set("search",search.trim());
      if(role)q.set("role",role);
      const data=await api.get<ApiList<Tiers>>("/api/v1/tiers/?"+q.toString());
      setItems(rows(data));
    }finally{setLoading(false);}
  }
  useEffect(()=>{void load();},[role]);

  const counts=useMemo(()=>({
    total:items.length,
    clients:items.filter(x=>x.roles.some(r=>r.actif&&r.role==="CLIENT")).length,
    suppliers:items.filter(x=>x.roles.some(r=>r.actif&&r.role==="SUPPLIER")).length,
    partners:items.filter(x=>x.roles.some(r=>r.actif&&r.role==="PARTNER")).length,
  }),[items]);
  const stats = [
    {value: counts.total, label: "Tous", icon: UsersRound},
    {value: counts.clients, label: "Clients", icon: UserRound},
    {value: counts.suppliers, label: "Fournisseurs", icon: Building2},
    {value: counts.partners, label: "Partenaires", icon: UsersRound},
  ];

  function addRole(code:TiersRoleCode|""){
    if(!code)return;
    setForm(current=>current.role_codes.includes(code)?current:{...current,role_codes:[...current.role_codes,code]});
  }
  function removeRole(code:TiersRoleCode){
    setForm(current=>({...current,role_codes:current.role_codes.filter(x=>x!==code)}));
  }

  async function create(e:FormEvent){
    e.preventDefault();setError("");
    if(!form.role_codes.length){setError("Sélectionnez au moins un rôle.");return;}
    try{
      const created=await api.post<Tiers>("/api/v1/tiers/",form);
      setModal(false);setForm(emptyForm);await load();navigate("/tiers/"+created.id);
    }catch(x){setError(x instanceof Error?x.message:"Création impossible.");}
  }

  return <div>
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div><div className="text-sm font-semibold text-[#E75E09]">Relations</div><h1 className="mt-1 text-3xl font-black">Tiers</h1><p className="mt-2 text-sm text-slate-500">Clients, fournisseurs, partenaires et contreparties de votre entreprise.</p></div>
      <button className="btn-primary flex items-center gap-2" onClick={()=>setModal(true)}><Plus size={17}/> Nouveau tiers</button>
    </div>

    <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {stats.map(({value,label,icon:Icon})=><div key={label} className="panel p-4"><div className="flex items-center justify-between"><span className="text-xs font-bold uppercase text-slate-400">{label}</span><Icon size={17} className="text-slate-400"/></div><div className="mt-2 text-2xl font-black">{value}</div></div>)}
    </div>

    <div className="panel mt-5 p-4">
      <div className="flex flex-col gap-3 md:flex-row">
        <form className="relative flex-1" onSubmit={e=>{e.preventDefault();void load();}}><Search size={17} className="absolute left-3 top-3 text-slate-400"/><input className="field pl-10" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nom, téléphone, email, code, identifiant fiscal…"/></form>
        <select className="field md:max-w-56" value={role} onChange={e=>setRole(e.target.value)}><option value="">Tous les rôles</option>{tiersRoles.map(([code,label])=><option key={code} value={code}>{label}</option>)}</select>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead><tr className="border-b border-slate-200 text-xs uppercase text-slate-400"><th className="px-3 py-3">Tiers</th><th className="px-3 py-3">Rôles</th><th className="px-3 py-3">Contact</th><th className="px-3 py-3">Code</th><th className="px-3 py-3"></th></tr></thead>
          <tbody>{items.map(item=><tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
            <td className="px-3 py-3"><div className="font-bold text-slate-900">{item.nom_affichage}</div><div className="text-xs text-slate-400">{item.type_tiers==="PERSON"?"Personne":"Organisation"}</div></td>
            <td className="px-3 py-3"><div className="flex flex-wrap gap-1">{item.roles.filter(r=>r.actif).map(r=><span key={r.id} className="badge bg-slate-100 text-slate-700">{tiersRoleLabels[r.role]}</span>)}</div></td>
            <td className="px-3 py-3"><div>{item.telephone||item.whatsapp||"—"}</div><div className="text-xs text-slate-400">{item.email}</div></td>
            <td className="px-3 py-3 font-mono text-xs">{item.code}</td>
            <td className="px-3 py-3 text-right"><button className="btn-secondary" onClick={()=>navigate("/tiers/"+item.id)}>Ouvrir</button></td>
          </tr>)}</tbody>
        </table>
        {!loading&&!items.length&&<div className="py-10 text-center text-sm text-slate-500">Aucun tiers trouvé.</div>}
        {loading&&<div className="py-10 text-center text-sm text-slate-500">Chargement…</div>}
      </div>
    </div>

    {modal&&<div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/45 p-4"><div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4"><div><div className="text-xs font-bold uppercase text-[#E75E09]">Tiers</div><h2 className="text-xl font-black">Nouveau tiers</h2></div><button className="rounded-xl p-2 hover:bg-slate-100" onClick={()=>setModal(false)}><X size={20}/></button></div>
      <form className="grid gap-4 p-5 sm:grid-cols-2" onSubmit={create}>
        {error&&<p className="sm:col-span-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{error}</p>}
        <label><span className="label">Type</span><select className="field" value={form.type_tiers} onChange={e=>setForm({...form,type_tiers:e.target.value as TiersType})}><option value="ORGANIZATION">Organisation</option><option value="PERSON">Personne</option></select></label>
        {form.type_tiers==="ORGANIZATION"?<label><span className="label">Raison sociale</span><input className="field" required value={form.raison_sociale} onChange={e=>setForm({...form,raison_sociale:e.target.value})}/></label>:<>
          <label><span className="label">Prénom</span><input className="field" value={form.prenom} onChange={e=>setForm({...form,prenom:e.target.value})}/></label>
          <label><span className="label">Nom</span><input className="field" required value={form.nom} onChange={e=>setForm({...form,nom:e.target.value})}/></label>
        </>}
        <label><span className="label">Téléphone</span><input className="field" value={form.telephone} onChange={e=>setForm({...form,telephone:e.target.value})}/></label>
        <label><span className="label">WhatsApp</span><input className="field" value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})}/></label>
        <label className="sm:col-span-2"><span className="label">Email</span><input className="field" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></label>
        <label><span className="label">Identifiant fiscal</span><input className="field" value={form.identifiant_fiscal} onChange={e=>setForm({...form,identifiant_fiscal:e.target.value})}/></label>
        <label><span className="label">Registre commerce</span><input className="field" value={form.registre_commerce} onChange={e=>setForm({...form,registre_commerce:e.target.value})}/></label>
        <div className="sm:col-span-2">
          <label><span className="label">Rôle</span><select className="field" value="" onChange={e=>addRole(e.target.value as TiersRoleCode|"")}><option value="">Sélectionner un rôle…</option>{tiersRoles.filter(([code])=>!form.role_codes.includes(code)).map(([code,label])=><option key={code} value={code}>{label}</option>)}</select></label>
          {form.role_codes.length>0&&<div className="mt-2 flex flex-wrap gap-1.5">{form.role_codes.map(code=><span key={code} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">{tiersRoleLabels[code]}<button type="button" className="grid size-4 place-items-center rounded hover:bg-slate-200" aria-label={`Retirer ${tiersRoleLabels[code]}`} title="Retirer" onClick={()=>removeRole(code)}><X size={11}/></button></span>)}</div>}
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2 border-t border-slate-100 pt-4"><button type="button" className="btn-secondary" onClick={()=>setModal(false)}>Annuler</button><button className="btn-primary">Créer le tiers</button></div>
      </form>
    </div></div>}
  </div>;
}
